/* -------------------------- Design imports start -------------------------- */
import { Grid, Typography } from '@mui/material'
import { type ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next'

import MultilevelDrawer from '../../components/layout/MultilevelDrawer'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */

import GetConstant from './utils/constants'
import LogTool from '../../logger/logTools'
import { missingRequiredFormFields } from '../../utils/functions'
import { fetchContactOptions, fetchAllContactOptions } from '../Contact/api/contactApi'
import { type Contact, type SelectOption } from '../../utils/types'
import { useCreateContact } from '../Contact/hooks/useContactMutations'
import ContactDrawer from '../Customer/ContactDrawer'
import { Form, type FormFieldType, type FormObject } from "../Form";
import { type Supplier } from "./utils/types";
import { useAllContacts } from '../Contact/hooks/useContactQueries'

/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: Supplier | undefined
    setState: React.Dispatch<React.SetStateAction<Supplier | undefined>>
  }
  onClose?: () => void
  onCreateConfirm: (input: Supplier) => void
  enableEditMode?: boolean
  onEditConfirm?: (input: Supplier) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function SupplierDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'SupplierModal', enable: true, logLevel: 'warn' })
  const {
    open,
    setOpen,
    formState,
    onClose = () => null,
    enableEditMode = false,
    onCreateConfirm = () => null,
    onEditConfirm = () => alert('TODO: provide a onEditConfirm callback!'),
  } = props
  const { t } = useTranslation()
  const createContactMutation = useCreateContact()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [prevOpen, setPrevOpen] = useState(false)
  const [openContactModal, setOpenContactModal] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const contactStore = useRef<Contact[]>([])
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [contactFormInput, setContactFormInput] = useState<Partial<Contact> | Contact | undefined>(undefined)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  const {
    data: contactsData,
    isError: contactsError,
    error: contactsErrorObj,
  } = useAllContacts()

  useEffect(() => {
    if (contactsError && contactsErrorObj) {
      log.error('Error fetching contacts', contactsErrorObj)
    }
  }, [contactsError, contactsErrorObj, log])

  useEffect(() => {
    if (!contactsData) return
    contactStore.current = contactsData
  }, [contactsData])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------ Callback functions start ------------------------ */
  /**
   * Callback that is executed when the modal is closed. Sets all modal state to the initial values
   * such that the modal is 'fresh' every time it is opened again.
   * @param preventOnClose prevent calling the onClose function. If true handleClose only resets all states!
   */
  const handleClose = (preventOnClose = false): void => {
    // reset modal to initial state
    formState.setState({})
    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleCreateConfirm = (): void => {
    // posting request to server and waiting on response
    if (!formState.state) {
      throw new Error('No form state found! Can\'t confirm create')
    }

    // TODO: handle adding contacts on confirm
    onCreateConfirm({ ...formState.state/*, contacts: [formState.state.contact?.value]*/ })
    handleClose(true)
  }

  const handleEditConfirm = (): void => {
    // posting request to server and waiting on response
    if (!formState.state) {
      throw new Error('No form state found! Can\'t confirm edit')
    }
    onEditConfirm(formState.state)
    handleClose(true)
  }

  const handleAddContactOption = async (contactFormInput: any): Promise<'SUCCESS' | 'ERROR'> => {
    log.info('Begin sending contact.')
    log.debug('Input ->', contactFormInput)

    try {
      // create new contact
      const response = await createContactMutation.mutateAsync(contactFormInput)

      // update contact options
      setContactOptions(prev => {
        return [
          {
            value: response.self,
            label:
              response.firstName && response.lastName
                ? `${response.firstName} ${response.lastName}`
                : response.email,
          },
          ...prev,
        ]
      })

      //set selected contact option
      // TODO: enable multi contact input and find a better way to keep track of select and server request data!
      formState.setState((prev: Supplier | undefined): Supplier => {
        return {
          ...prev,
          contact: {
            value: response.self,
            label:
              response.firstName && response.lastName
                ? `${response.firstName} ${response.lastName}`
                : response.email,
          }, // contact stores the select value and label
          contacts: [response.self], // contacts stores the contact list that is send to the server
          isNewContact: true,
        }
      })

      return 'SUCCESS'
    } catch (error) {
      log.error('Error on creating contact option:', error)
      return 'ERROR'
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const formFieldsSupplier: FormFieldType[] = GetConstant({
    name: 'supplierFormFields',
  }) as FormFieldType[]
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  if (open && !prevOpen) {
    setPrevOpen(true)

    setContactOptions(
      contactStore.current.map(contact => {
        return {
          value: contact.self,
          label:
            contact.firstName && contact.lastName
              ? `${contact.firstName} ${contact.lastName}`
              : contact.email,
        }
      })
    )
  }
  /* ------------------------- Pre render actions end ------------------------- */
  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (!formState.state) {
    return <></>
  }

  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        size="small"
        onClose={handleClose}
        title={t('supplier:interaction.button.addSupplier') }
        confirmButtonProps={{
          text: enableEditMode
            ? t('common:interaction.button.save')
            : t('common:interaction.button.add'),
          onConfirm: () => {
            enableEditMode
              ? handleEditConfirm()
              : handleCreateConfirm()
          },
          disabled: missingRequiredFormFields(formFieldsSupplier, formState.state),
        }}
      >
        <Grid container spacing={2}>
          <Form
            editing={true}
            formFields={formFieldsSupplier}
            formObject={formState.state}
            onChange={(value: FormObject) => formState.setState(value as Supplier)}
          />
        </Grid>
        {!enableEditMode && (
          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            {t('supplier:content.label.contactData')}
          </Typography>
        )}
        {!enableEditMode && (
          <Grid container>
            <Form
              editing={true}
              formFields={[
                {
                  type: 'select-search',
                  key: 'contact',
                  label: t('common:content.label.contact'),
                  autocompleteField: 'contacts',
                  onAddOption: () => setOpenContactModal(true),
                  xl: 12,
                  sm: 12,
                  md: 12,
                  lg: 12,
                },
              ]}
              formObject={formState.state}
              onChange={(value: FormObject) => formState.setState(value as Supplier)}
            />
          </Grid>
        )}
      </MultilevelDrawer>
      <ContactDrawer
        open={openContactModal}
        setOpen={setOpenContactModal}
        formState={{ state: contactFormInput, setState: setContactFormInput }}
        onConfirm={handleAddContactOption}
      />
    </>
  )
}
